import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStorefrontContext } from 'context/StorefrontContext';
import { interpolateAuthText, pickAuthText } from 'utils/authPage';
import { resolveSiteName, resolveStoreMediaUrl } from 'utils/themeDisplay';

export const useAuthPageCopy = () => {
  const { t } = useTranslation('account');
  const { authPage, theme } = useStorefrontContext();
  const siteName = resolveSiteName(theme);

  return useMemo(() => {
    const withSite = (value: string) => interpolateAuthText(value, siteName);
    const logoSrc = authPage.showLogo
      ? resolveStoreMediaUrl(authPage.useThemeLogo ? theme?.logoUrl : authPage.logoUrl)
      : '';

    return {
      ...authPage,
      siteName,
      logoSrc,
      panelBackgroundSrc: resolveStoreMediaUrl(authPage.panelBackgroundImageUrl),
      panelTitle: withSite(pickAuthText(authPage.panelTitle, t('authPanelTitle', { siteName }), true)),
      panelSubtitle: withSite(pickAuthText(authPage.panelSubtitle, t('authPanelSubtitle'), true)),
      title: withSite(pickAuthText(authPage.title, t('login'))),
      subtitle: withSite(pickAuthText(authPage.subtitle, t('loginSubtitle'), true)),
      submitLabel: pickAuthText(authPage.submitLabel, t('login')),
      loggingInLabel: pickAuthText(authPage.loggingInLabel, t('loggingIn')),
      emailLabel: pickAuthText(authPage.emailLabel, t('email')),
      passwordLabel: pickAuthText(authPage.passwordLabel, t('password')),
      forgotPasswordLabel: pickAuthText(authPage.forgotPasswordLabel, t('forgotPassword')),
      noAccountText: pickAuthText(authPage.noAccountText, t('noAccount'), true),
      registerButtonLabel: pickAuthText(authPage.registerButtonLabel, t('createAccount')),
      orText: pickAuthText(authPage.orText, t('or')),
      b2bLinkLabel: pickAuthText(authPage.b2bLinkLabel, t('b2bRequestLink')),
      backToStoreLabel: pickAuthText(authPage.backToStoreLabel, t('backToStore')),
      copyrightText: withSite(
        pickAuthText(authPage.copyrightText, t('authCopyright', { siteName, year: new Date().getFullYear() })),
      ),
    };
  }, [authPage, siteName, t, theme?.logoUrl]);
};
